import {
  RecordType,
  VehicleBodyTypes,
  VehicleColorTypes,
  VehicleMakeTypes,
} from '@lib/vehicle-intelligence/api/constants/parameters';

export interface SelectOption<T> {
  label: string;
  value: T;
}

// Form Constants
export const FIELD_APPEARANCE_OUTLINE = 'outline' as const;
export const MIN_REASON_LENGTH = 4;

// Record Type Options
export const HIT_READ_OPTIONS: SelectOption<RecordType>[] = [
  { label: 'Hits Only', value: RecordType.HIT },
  { label: 'All Detections', value: RecordType.ALL },
];

export const VEHICLE_BODY_TYPE: SelectOption<VehicleBodyTypes>[] = [
  { label: 'Car - Sedan', value: VehicleBodyTypes.SEDAN },
  { label: 'Car - Coupe', value: VehicleBodyTypes.COUPE },
  { label: 'Car - Convertible', value: VehicleBodyTypes.CONVERTIBLE },
  { label: 'Car - Hatchback', value: VehicleBodyTypes.HATCHBACK },
  { label: 'Car - Wagon', value: VehicleBodyTypes.WAGON },
  { label: 'SUV', value: VehicleBodyTypes.SUV },
  { label: 'Pickup Truck', value: VehicleBodyTypes.PICKUP },
  { label: 'Van - Minivan', value: VehicleBodyTypes.MINIVAN },
  { label: 'Van - Work Van', value: VehicleBodyTypes.WORK_VAN },
  { label: 'Semi Truck', value: VehicleBodyTypes.SEMI_TRUCK },
  { label: 'Bus', value: VehicleBodyTypes.BUS },
  { label: 'Motorcycle', value: VehicleBodyTypes.MOTORCYCLE },
  { label: 'Golf Cart / ATV', value: VehicleBodyTypes.GOLF_CART },
  { label: 'Trailer', value: VehicleBodyTypes.TRAILER },
  { label: 'Bicycle', value: VehicleBodyTypes.BICYCLE },
  { label: 'Box Truck', value: VehicleBodyTypes.BOX_TRUCK },
].sort((a, b) => a.label.localeCompare(b.label));

export const VEHICLE_COLOR: SelectOption<VehicleColorTypes>[] = [
  { label: 'White', value: VehicleColorTypes.WHITE },
  { label: 'Silver/Grey', value: VehicleColorTypes.SILVER_GREY },
  { label: 'Black', value: VehicleColorTypes.BLACK },
  { label: 'Blue', value: VehicleColorTypes.BLUE },
  { label: 'Red', value: VehicleColorTypes.RED },
  { label: 'Brown', value: VehicleColorTypes.BROWN },
  { label: 'Yellow', value: VehicleColorTypes.YELLOW },
  { label: 'Green', value: VehicleColorTypes.GREEN },
].sort((a, b) => a.label.localeCompare(b.label));

export const VEHICLE_MAKE: SelectOption<VehicleMakeTypes>[] = [
  { label: 'Acura', value: VehicleMakeTypes.ACURA },
  { label: 'Audi', value: VehicleMakeTypes.AUDI },
  { label: 'Bentley', value: VehicleMakeTypes.BENTLEY },
  { label: 'BMW', value: VehicleMakeTypes.BMW },
  { label: 'Buick', value: VehicleMakeTypes.BUICK },
  { label: 'Cadillac', value: VehicleMakeTypes.CADILLAC },
  { label: 'Chevrolet', value: VehicleMakeTypes.CHEVROLET },
  { label: 'Chrysler', value: VehicleMakeTypes.CHRYSLER },
  { label: 'Dodge', value: VehicleMakeTypes.DODGE },
  { label: 'Ferrari', value: VehicleMakeTypes.FERRARI },
  { label: 'Fiat', value: VehicleMakeTypes.FIAT },
  { label: 'Ford', value: VehicleMakeTypes.FORD },
  { label: 'GMC', value: VehicleMakeTypes.GMC },
  { label: 'Honda', value: VehicleMakeTypes.HONDA },
  { label: 'Hummer', value: VehicleMakeTypes.HUMMER },
  { label: 'Hyundai', value: VehicleMakeTypes.HYUNDAI },
  { label: 'Infiniti', value: VehicleMakeTypes.INFINITI },
  { label: 'Jaguar', value: VehicleMakeTypes.JAGUAR },
  { label: 'Jeep', value: VehicleMakeTypes.JEEP },
  { label: 'Kia', value: VehicleMakeTypes.KIA },
  { label: 'Land Rover', value: VehicleMakeTypes.LAND_ROVER },
  { label: 'Lexus', value: VehicleMakeTypes.LEXUS },
  { label: 'Lincoln', value: VehicleMakeTypes.LINCOLN },
  { label: 'Maserati', value: VehicleMakeTypes.MASERATI },
  { label: 'Mazda', value: VehicleMakeTypes.MAZDA },
  { label: 'Mercedes', value: VehicleMakeTypes.MERCEDES },
  { label: 'Mercury', value: VehicleMakeTypes.MERCURY },
  { label: 'Mini', value: VehicleMakeTypes.MINI },
  { label: 'Mitsubishi', value: VehicleMakeTypes.MITSUBISHI },
  { label: 'Nissan', value: VehicleMakeTypes.NISSAN },
  { label: 'Oldsmobile', value: VehicleMakeTypes.OLDSMOBILE },
  { label: 'Other', value: VehicleMakeTypes.OTHER },
  { label: 'Pontiac', value: VehicleMakeTypes.PONTIAC },
  { label: 'Porsche', value: VehicleMakeTypes.PORSCHE },
  { label: 'RAM', value: VehicleMakeTypes.RAM },
  { label: 'Saab', value: VehicleMakeTypes.SAAB },
  { label: 'Saturn', value: VehicleMakeTypes.SATURN },
  { label: 'Scion', value: VehicleMakeTypes.SCION },
  { label: 'Smart', value: VehicleMakeTypes.SMART },
  { label: 'Subaru', value: VehicleMakeTypes.SUBARU },
  { label: 'Suzuki', value: VehicleMakeTypes.SUZUKI },
  { label: 'Tesla', value: VehicleMakeTypes.TESLA },
  { label: 'Toyota', value: VehicleMakeTypes.TOYOTA },
  { label: 'Volkswagen', value: VehicleMakeTypes.VOLKSWAGEN },
  { label: 'Volvo', value: VehicleMakeTypes.VOLVO },
].sort((a, b) => a.label.localeCompare(b.label));

// US States
export const US_STATES: SelectOption<string>[] = [
  { label: 'AL', value: 'alabama' },
  { label: 'AK', value: 'alaska' },
  { label: 'AS', value: 'american_samoa' },
  { label: 'AZ', value: 'arizona' },
  { label: 'AR', value: 'arkansas' },
  { label: 'CA', value: 'california' },
  { label: 'CO', value: 'colorado' },
  { label: 'CT', value: 'connecticut' },
  { label: 'DE', value: 'delaware' },
  { label: 'DC', value: 'district_of_columbia' },
  { label: 'FM', value: 'federated_states_of_micronesia' },
  { label: 'FL', value: 'florida' },
  { label: 'GA', value: 'georgia' },
  { label: 'GU', value: 'guam' },
  { label: 'HI', value: 'hawaii' },
  { label: 'ID', value: 'idaho' },
  { label: 'IL', value: 'illinois' },
  { label: 'IN', value: 'indiana' },
  { label: 'IA', value: 'iowa' },
  { label: 'KS', value: 'kansas' },
  { label: 'KY', value: 'kentucky' },
  { label: 'LA', value: 'louisiana' },
  { label: 'ME', value: 'maine' },
  { label: 'MH', value: 'marshall_islands' },
  { label: 'MD', value: 'maryland' },
  { label: 'MA', value: 'massachusetts' },
  { label: 'MI', value: 'michigan' },
  { label: 'MN', value: 'minnesota' },
  { label: 'MS', value: 'mississippi' },
  { label: 'MO', value: 'missouri' },
  { label: 'MT', value: 'montana' },
  { label: 'NE', value: 'nebraska' },
  { label: 'NV', value: 'nevada' },
  { label: 'NH', value: 'new_hampshire' },
  { label: 'NJ', value: 'new_jersey' },
  { label: 'NM', value: 'new_mexico' },
  { label: 'NY', value: 'new_york' },
  { label: 'NC', value: 'north_carolina' },
  { label: 'ND', value: 'north_dakota' },
  { label: 'MP', value: 'northern_mariana_islands' },
  { label: 'OH', value: 'ohio' },
  { label: 'OK', value: 'oklahoma' },
  { label: 'OR', value: 'oregon' },
  { label: 'PW', value: 'palau' },
  { label: 'PA', value: 'pennsylvania' },
  { label: 'PR', value: 'puerto_rico' },
  { label: 'RI', value: 'rhode_island' },
  { label: 'SC', value: 'south_carolina' },
  { label: 'SD', value: 'south_dakota' },
  { label: 'TN', value: 'tennessee' },
  { label: 'TX', value: 'texas' },
  { label: 'UT', value: 'utah' },
  { label: 'VT', value: 'vermont' },
  { label: 'VI', value: 'virgin_islands' },
  { label: 'VA', value: 'virginia' },
  { label: 'WA', value: 'washington' },
  { label: 'WV', value: 'west_virginia' },
  { label: 'WI', value: 'wisconsin' },
  { label: 'WY', value: 'wyoming' },
].sort((a, b) => a.label.localeCompare(b.label));
